import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { ImportedEvent } from "@myams/shared";
import { MA_CATS } from "@myams/shared";
import { MapView } from "../components/MapView";
import {
  MaCircleButton, MaIcon, MaImageSlot, MaLiveBadge, MaSectionHeader,
} from "../components/ui";
import {
  useAuthStatus, useEventSaves, useImportedEvent, useImportedEvents, useToggleEventSave,
} from "../lib/queries";
import { directionsUrl, share } from "../lib/share";
import { formatEventDateRange } from "../lib/time";

// Carried in react-router location.state when this screen is reached
// from a list (Search, NowFeed). Enables swipe / chevron navigation
// between sibling events without losing the back-to-list affordance.
interface EventSwipeState {
  siblings?: string[];
  index?: number;
}

// Animation timings. Tuned for snappy iOS-feel. Lower than the previous
// 320 ms — past ~280 ms the commit reads as a "load animation" rather
// than direct manipulation.
const COMMIT_MS = 240;
const SNAP_BACK_MS = 200;
const SWIPE_EASE = "cubic-bezier(.2,.7,.2,1)";

function Toast({ msg }: { msg: string }) {
  return (
    <div style={{
      position: "absolute", bottom: 100, left: "50%", transform: "translateX(-50%)",
      background: "var(--ma-ink)", color: "var(--ma-paper)",
      padding: "10px 16px", borderRadius: "var(--ma-card-radius)",
      fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
      textTransform: "uppercase", zIndex: 50,
      boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
    }}>{msg}</div>
  );
}

export function EventDetail() {
  // The event id is "<source>:<source-id>" — encoded as %3A in the URL.
  // react-router-dom v6 decodes useParams() values so we get the colon back.
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const swipeState = (location.state as EventSwipeState | null) ?? null;
  const hasExplicitSiblings = !!swipeState?.siblings && swipeState.siblings.length > 1;

  // Fallback siblings for deep-link arrivals (no location.state — e.g.
  // user pasted the URL in a fresh browser tab). We pull the public
  // upcoming list and use that as the carousel context if the current
  // event is in it. Same params as Search uses so the cache is shared
  // and this is free after the first load.
  const { data: upcomingList = [] } = useImportedEvents({ upcoming: true, limit: 100 });
  const derived = useMemo(() => {
    if (hasExplicitSiblings) return null;
    if (!id || upcomingList.length === 0) return null;
    const ids = upcomingList.map((e) => e.id);
    const idx = ids.indexOf(id);
    if (idx === -1) return null;     // past event, or simply not in the list
    return { siblings: ids, index: idx };
  }, [hasExplicitSiblings, upcomingList, id]);

  const siblings = swipeState?.siblings ?? derived?.siblings;
  const sIndex = swipeState?.index ?? derived?.index ?? -1;
  const canSwipe = !!siblings && siblings.length > 1 && sIndex >= 0;

  // Three-pane carousel: prev / current / next. Each fetches its own
  // event via useImportedEvent; TanStack Query caches by id so coming
  // back to an already-seen event is free.
  const prevId = canSwipe && sIndex > 0 ? siblings![sIndex - 1] : undefined;
  const nextId = canSwipe && sIndex < siblings!.length - 1 ? siblings![sIndex + 1] : undefined;
  const { data: ev, isPending, isError } = useImportedEvent(id);
  const { data: prevEv } = useImportedEvent(prevId);
  const { data: nextEv } = useImportedEvent(nextId);

  // Save / share state. Only the centered pane gets active action
  // wiring; prev/next panes render the same buttons but they're
  // no-ops there (user has to swipe in first).
  const { data: eventSaves = [] } = useEventSaves();
  const toggleEventSave = useToggleEventSave();
  const { status: authStatus } = useAuthStatus();
  const [toast, setToast] = useState<string | null>(null);
  const isSaved = !!id && eventSaves.some((e) => e.id === id);

  // Drag state for the track. trackRef gets transform mutations
  // imperatively during the drag — we never call setState on
  // pointermove, that would re-render the whole screen at 60 Hz.
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    startX: number; startY: number; startT: number;
    axis: "h" | "v" | null;
    pointerId: number;
    /** The element that received pointerdown — used to call
     *  setPointerCapture once we know it's a horizontal swipe. */
    el: HTMLDivElement;
    captured: boolean;
  } | null>(null);

  // Measured width of the .ma-screen container. Critical: we can't use
  // 100vw because on desktop the phone-frame clamps .ma-frame to 440 px
  // wide — a "100vw" pane is then ~1920 px and only a ~440 px slice
  // shows through the frame (the carousel was rendering with the title
  // mid-cut). ResizeObserver keeps this in sync if the frame resizes
  // (window resize, device rotation, dev-tools toggle).
  //
  // The element is held in state (not a useRef) so the measuring
  // effect re-runs when the carousel finally mounts. On first render
  // we're showing the "loading…" screen (no .ma-screen with this ref)
  // and a plain useRef + useLayoutEffect([]) would miss the actual
  // carousel mount, leaving paneW stuck at window.innerWidth.
  const [screenEl, setScreenEl] = useState<HTMLDivElement | null>(null);
  const [paneW, setPaneW] = useState<number>(() =>
    typeof window === "undefined" ? 480 : window.innerWidth
  );
  useLayoutEffect(() => {
    if (!screenEl) return;
    if (screenEl.clientWidth > 0) setPaneW(screenEl.clientWidth);
    const ro = new ResizeObserver(() => {
      if (screenEl.clientWidth > 0) setPaneW(screenEl.clientWidth);
    });
    ro.observe(screenEl);
    return () => ro.disconnect();
  }, [screenEl]);

  /** translate3d the track by `dxPx` relative to its centered position
   *  (which is `-paneW` — second pane on screen). */
  const setTrackOffset = (dxPx: number, transition: string = "none") => {
    const el = trackRef.current;
    if (!el) return;
    el.style.transition = transition;
    el.style.transform = `translate3d(${-paneW + dxPx}px, 0, 0)`;
  };

  /** Snap the track back to centered with no animation. Called from
   *  useLayoutEffect after navigate(replace) so the user never sees
   *  the post-commit offset frame. */
  const recenter = () => {
    const el = trackRef.current;
    if (!el) return;
    el.style.transition = "none";
    el.style.transform = `translate3d(${-paneW}px, 0, 0)`;
    // Force a reflow so the next change uses a transition (rather than
    // the browser collapsing "none then transition" into one paint).
    void el.offsetWidth;
  };

  const slideAndGo = (dir: -1 | 1) => {
    if (!canSwipe) return;
    const next = sIndex + dir;
    if (next < 0 || next >= siblings!.length) return;
    setTrackOffset(dir < 0 ? paneW : -paneW, `transform ${COMMIT_MS}ms ${SWIPE_EASE}`);
    window.setTimeout(() => {
      navigate(`/event/${encodeURIComponent(siblings![next]!)}`, {
        state: { siblings, index: next } satisfies EventSwipeState,
        replace: true,
      });
    }, COMMIT_MS);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canSwipe) return;
    dragRef.current = {
      startX: e.clientX, startY: e.clientY, startT: Date.now(),
      axis: null, pointerId: e.pointerId,
      el: e.currentTarget as HTMLDivElement,
      captured: false,
    };
    // Deliberately NOT calling setPointerCapture here. On desktop mice
    // an unconditional capture redirects pointerup to the track, the
    // browser can't generate a click on the originally-targeted
    // <button>, and every action button on this page stops firing. We
    // capture once axis is locked to "h" instead — see onPointerMove.
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.axis) {
      // 8 px dead zone before the axis is locked. Vertical-leaning
      // gestures pass through to the pane's own overflow:auto.
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      d.axis = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      // Capture the pointer NOW (axis just locked horizontal) so the
      // remainder of the gesture stays with the track even if the
      // finger leaves it. Deferred from pointerdown specifically so a
      // tap-without-drag never captures, which would otherwise prevent
      // the click event from firing on the actual button target.
      if (d.axis === "h" && !d.captured) {
        d.el.setPointerCapture?.(e.pointerId);
        d.captured = true;
      }
    }
    if (d.axis !== "h") return;

    // Rubber-band at the ends: 30 % gain past first/last sibling.
    let clamped = dx;
    if (dx > 0 && sIndex <= 0) clamped = dx * 0.3;
    if (dx < 0 && sIndex >= siblings!.length - 1) clamped = dx * 0.3;
    setTrackOffset(clamped);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || e.pointerId !== d.pointerId || d.axis !== "h") {
      setTrackOffset(0);
      return;
    }
    const dx = e.clientX - d.startX;
    const dt = Math.max(1, Date.now() - d.startT);
    const velocity = Math.abs(dx) / dt;
    const threshold = Math.min(paneW * 0.3, 140);
    const fast = velocity > 0.5;
    const committed = Math.abs(dx) > threshold || (fast && Math.abs(dx) > 40);

    if (committed && dx < 0 && sIndex < siblings!.length - 1) {
      slideAndGo(1);
    } else if (committed && dx > 0 && sIndex > 0) {
      slideAndGo(-1);
    } else {
      setTrackOffset(0, `transform ${SNAP_BACK_MS}ms ${SWIPE_EASE}`);
    }
  };

  // After navigate(replace), the new render has shifted prev/current/
  // next. The track is at translate(-2w) (just committed) but the
  // user's target event is now in the CENTER pane, so we snap to
  // translate(-w). useLayoutEffect runs synchronously before paint so
  // the user never sees an intermediate frame.
  useLayoutEffect(() => {
    if (!canSwipe) return;
    recenter();
    // Re-run on id change (post-navigate) AND on paneW change (so a
    // resize that re-measures the frame snaps the track to the right
    // new centre, since the pixel transform is paneW-dependent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, paneW]);

  // Actions — only wired on the centered (active) pane's body, but
  // defined at the top so we can pass them down.
  const onShare = async () => {
    if (!ev) return;
    const url = typeof window !== "undefined" ? window.location.href : `https://myams.pages.dev/event/${ev.id}`;
    const result = await share({ title: ev.title, text: ev.description ?? "", url });
    if (result === "copied") setToast("Link copied");
    else if (result === "shared") setToast("Shared");
    else setToast("Couldn't share");
    setTimeout(() => setToast(null), 1800);
  };

  const onDirections = () => {
    if (!ev || ev.lat == null || ev.lng == null) {
      setToast("No location"); setTimeout(() => setToast(null), 1800); return;
    }
    window.open(directionsUrl(ev.lat, ev.lng, ev.title), "_blank", "noopener");
  };

  const onToggleSave = () => {
    if (!ev) return;
    if (authStatus !== "authed") {
      navigate("/login", { state: { returnTo: location.pathname + location.search } });
      return;
    }
    toggleEventSave.mutate(
      { eventId: ev.id, saved: isSaved },
      {
        onSuccess: () => {
          setToast(isSaved ? "Removed" : "Saved");
          setTimeout(() => setToast(null), 1500);
        },
        onError: () => { setToast("Couldn't save"); setTimeout(() => setToast(null), 1800); },
      }
    );
  };

  if (isPending && !ev) {
    return (
      <div className="ma-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", letterSpacing: "0.16em" }}>loading…</span>
      </div>
    );
  }

  if (isError || !ev) {
    return (
      <div className="ma-screen" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div className="ma-display" style={{ fontSize: 26, marginBottom: 12, textAlign: "center" }}>Event not found</div>
        <button onClick={() => navigate(-1)} style={{
          background: "var(--ma-ink)", color: "var(--ma-paper)", border: "none",
          padding: "12px 24px", borderRadius: "var(--ma-card-radius)",
          fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
          textTransform: "uppercase", cursor: "pointer",
        }}>
          Go back
        </button>
      </div>
    );
  }

  // ── Single-event fast path: no siblings → no carousel. Renders
  // just one body, no track wrapper. Cheaper, identical to the
  // pre-carousel behaviour.
  if (!canSwipe) {
    return (
      <div className="ma-screen" style={{ overflowY: "auto", paddingBottom: 110 }}>
        <EventBody
          event={ev}
          active
          isSaved={isSaved}
          onShare={onShare}
          onDirections={onDirections}
          onToggleSave={onToggleSave}
          onBack={() => navigate(-1)}
          siblings={null}
          sIndex={-1}
          onSlide={() => {}}
        />
        {toast && <Toast msg={toast} />}
      </div>
    );
  }

  // ── Carousel path. Track holds three siblings. Each pane scrolls
  // independently. Map renders only on the centered pane to avoid
  // multiplying WebGL contexts and tile fetches.
  return (
    <div
      ref={setScreenEl}
      className="ma-screen"
      style={{
        overflow: "hidden",
        touchAction: "pan-y",
      }}
    >
      <div
        ref={trackRef}
        style={{
          display: "flex", height: "100%",
          flexWrap: "nowrap",
          // Pixel-based transform off the MEASURED pane width (.ma-screen
          // clientWidth) — not 100vw. On desktop the phone-frame clamps
          // .ma-frame to 440 px so 100vw panes would be ~1920 px and the
          // title would cut off mid-word. ResizeObserver above re-syncs
          // paneW when the frame resizes.
          transform: `translate3d(${-paneW}px, 0, 0)`,
          willChange: "transform",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <Pane width={paneW}>
          {prevEv ? (
            <EventBody event={prevEv} active={false} isSaved={false}
              onShare={() => {}} onDirections={() => {}} onToggleSave={() => {}}
              onBack={() => {}} siblings={siblings ?? null} sIndex={sIndex - 1}
              onSlide={() => {}} />
          ) : <EdgePane direction="left" />}
        </Pane>

        <Pane width={paneW}>
          <EventBody
            event={ev}
            active
            isSaved={isSaved}
            onShare={onShare}
            onDirections={onDirections}
            onToggleSave={onToggleSave}
            onBack={() => navigate(-1)}
            siblings={siblings ?? null}
            sIndex={sIndex}
            onSlide={slideAndGo}
          />
        </Pane>

        <Pane width={paneW}>
          {nextEv ? (
            <EventBody event={nextEv} active={false} isSaved={false}
              onShare={() => {}} onDirections={() => {}} onToggleSave={() => {}}
              onBack={() => {}} siblings={siblings ?? null} sIndex={sIndex + 1}
              onSlide={() => {}} />
          ) : <EdgePane direction="right" />}
        </Pane>
      </div>
      {toast && <Toast msg={toast} />}
    </div>
  );
}

/** Carousel column for one EventBody. Width is the parent's MEASURED
 *  pixel width — see paneW upstream. Using 100vw would overflow the
 *  phone-frame on desktop and chop the content in half. */
function Pane({ width, children }: { width: number; children: React.ReactNode }) {
  return (
    <div style={{
      flex: `0 0 ${width}px`,
      width: `${width}px`,
      height: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      // overscroll-behavior contains the iOS rubber-band bounce
      // inside this pane so it doesn't leak up to the body.
      overscrollBehavior: "contain",
    }}>
      {children}
    </div>
  );
}

/** Placeholder shown at the boundaries (no prev / no next). Keeps the
 *  track width correct and gives the user a visual cue when they try
 *  to swipe off the ends. */
function EdgePane({ direction }: { direction: "left" | "right" }) {
  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 6, padding: 24,
      background: "var(--ma-paper)",
      color: "var(--ma-ink-soft)",
    }}>
      <div className="ma-mono" style={{
        fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
      }}>
        {direction === "left" ? "start of list" : "end of list"}
      </div>
      <div style={{ fontFamily: "var(--ma-body)", fontSize: 13, textAlign: "center", lineHeight: 1.45 }}>
        {direction === "left" ? "Swipe right to come back" : "Swipe back to see more"}
      </div>
    </div>
  );
}

/** All the content for one event. Used three times in the carousel.
 *  `active=false` means non-centered: action buttons render but are
 *  no-ops, and the map is omitted to keep prev/next cheap (no extra
 *  WebGL contexts). */
function EventBody({
  event, active, isSaved, onShare, onDirections, onToggleSave, onBack,
  siblings, sIndex, onSlide,
}: {
  event: ImportedEvent;
  active: boolean;
  isSaved: boolean;
  onShare: () => void;
  onDirections: () => void;
  onToggleSave: () => void;
  onBack: () => void;
  siblings: string[] | null;
  sIndex: number;
  onSlide: (dir: -1 | 1) => void;
}) {
  const cat = MA_CATS[event.category ?? "all"] ?? MA_CATS.all;
  const tone: "brick" | "canal" | "mustard" | "pink" =
    event.category === "music" ? "brick"
    : event.category === "art" ? "canal"
    : event.category === "queer" || event.category === "cinema" ? "pink"
    : "mustard";
  const hasMap = event.lat != null && event.lng != null;
  const showSiblingNav = !!siblings && siblings.length > 1 && sIndex >= 0;

  return (
    <div style={{ paddingBottom: 110 }}>
      <div style={{ position: "relative", height: 320, overflow: "hidden" }}>
        <MaImageSlot
          height={320}
          tone={tone}
          src={event.imageUrl ?? null}
          category={event.category ?? null}
          fallbackInitial={event.title}
        />
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, rgba(20,12,4,0.5) 0%, transparent 30%, transparent 60%, rgba(20,12,4,0.6) 100%)",
        }} />

        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          padding: "calc(env(safe-area-inset-top, 20px) + 34px) 14px 0",
          display: "flex", justifyContent: "space-between",
          // pointer-events stay on so back tap on the centered pane
          // works; on inactive panes the handlers are no-ops anyway.
        }}>
          <MaCircleButton icon="back" label="Back" onClick={onBack} />
          <div style={{ display: "flex", gap: 8 }}>
            <MaCircleButton icon="share" label="Share" onClick={onShare} />
            <MaCircleButton
              icon="bookmark"
              label={isSaved ? "Saved" : "Save"}
              dark={isSaved}
              onClick={onToggleSave}
            />
          </div>
        </div>

        <div style={{ position: "absolute", top: 116, left: 16 }}>
          <MaLiveBadge>{event.source.toUpperCase()}</MaLiveBadge>
        </div>
      </div>

      <div style={{
        background: "var(--ma-paper)", padding: "20px 20px 16px",
        marginTop: -16, borderTopLeftRadius: 22, borderTopRightRadius: 22,
        position: "relative", zIndex: 2,
      }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.18em",
          textTransform: "uppercase", color: cat.color, marginBottom: 6,
        }}>
          {cat.label}{event.neighborhood ? ` · ${event.neighborhood}` : ""}
        </div>
        <div className="ma-display" style={{ fontSize: 32, color: "var(--ma-ink)", lineHeight: 0.95, marginBottom: 12 }}>
          {event.title}
        </div>
        {event.startsAt && (
          <div className="ma-mono" style={{
            fontSize: 11, letterSpacing: "0.06em",
            color: "var(--ma-ink-soft)",
          }}>
            {formatEventDateRange(event.startsAt, event.endsAt ?? null)}
          </div>
        )}

        {showSiblingNav && (
          <div style={{
            marginTop: 14, display: "flex", alignItems: "center", gap: 12,
            paddingTop: 12, borderTop: "1px dashed var(--ma-line)",
          }}>
            <button
              type="button"
              onClick={() => onSlide(-1)}
              disabled={!active || sIndex <= 0}
              aria-label="Previous event"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "transparent",
                border: "var(--ma-border) solid var(--ma-ink)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: (!active || sIndex <= 0) ? "not-allowed" : "pointer",
                opacity: (!active || sIndex <= 0) ? 0.35 : 1,
                color: "var(--ma-ink)", padding: 0,
              }}>
              <MaIcon name="back" size={14} stroke={2} />
            </button>
            <div className="ma-mono" style={{
              flex: 1, textAlign: "center", fontSize: 11,
              letterSpacing: "0.18em", textTransform: "uppercase",
              color: "var(--ma-ink-soft)",
            }}>
              {sIndex + 1} of {siblings!.length} · swipe ←→
            </div>
            <button
              type="button"
              onClick={() => onSlide(1)}
              disabled={!active || sIndex >= siblings!.length - 1}
              aria-label="Next event"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "transparent",
                border: "var(--ma-border) solid var(--ma-ink)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: (!active || sIndex >= siblings!.length - 1) ? "not-allowed" : "pointer",
                opacity: (!active || sIndex >= siblings!.length - 1) ? 0.35 : 1,
                color: "var(--ma-ink)", padding: 0, transform: "scaleX(-1)",
              }}>
              <MaIcon name="back" size={14} stroke={2} />
            </button>
          </div>
        )}
      </div>

      <div style={{ padding: "0 16px 18px", display: "flex", gap: 8 }}>
        <button
          onClick={onDirections}
          disabled={!hasMap || !active}
          style={{
            flex: 2, padding: "16px 0",
            background: hasMap ? "var(--ma-brick)" : "var(--ma-ink-soft)",
            color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 15,
            letterSpacing: "0.02em",
            cursor: (hasMap && active) ? "pointer" : "not-allowed",
            opacity: hasMap ? 1 : 0.6,
          }}>
          {hasMap ? "Directions" : "No location"}
        </button>
        <a
          href={active ? event.url : undefined}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!active}
          style={{
            flex: 1, padding: "16px 0",
            background: "var(--ma-cream)", color: "var(--ma-ink)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
            textTransform: "uppercase", fontWeight: 600,
            cursor: active ? "pointer" : "not-allowed",
            opacity: active ? 1 : 0.5,
            textAlign: "center", textDecoration: "none",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            pointerEvents: active ? "auto" : "none",
          }}>
          Source
          <MaIcon name="arrow" size={12} stroke={2.2} />
        </a>
      </div>

      {event.description && (
        <div style={{ padding: "0 16px 18px" }}>
          <MaSectionHeader label="About" />
          <div style={{
            fontFamily: "var(--ma-body)", fontSize: 14.5, lineHeight: 1.5,
            color: "var(--ma-ink)", whiteSpace: "pre-line",
          }}>
            {event.description}
          </div>
        </div>
      )}

      {/* Map renders on every pane (not just active) — the post-
          commit "map suddenly appears" frame was the actual jolt
          users perceived as carousel jagginess. 3 WebGL contexts on
          a modern phone is fine (Safari caps around 8); the cost is
          paid once on event-detail mount, not per-swipe. */}
      {hasMap && (
        <div style={{ padding: "0 16px 18px" }}>
          <MaSectionHeader label="Location" />
          <div style={{
            position: "relative",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            overflow: "hidden", height: 220,
          }}>
            <MapView
              pins={[{ id: event.id, title: event.title, lat: event.lat!, lng: event.lng!, cat: event.category ?? null, live: true }]}
              center={[event.lng!, event.lat!]}
              zoom={15}
              interactive={false}
            />
          </div>
          <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", marginTop: 6, letterSpacing: "0.06em" }}>
            {event.lat!.toFixed(4)}, {event.lng!.toFixed(4)}{event.address ? ` · ${event.address}` : ""}
          </div>
        </div>
      )}

      {event.rawTags.length > 0 && (
        <div style={{ padding: "0 16px 24px" }}>
          <MaSectionHeader label="Tags" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {event.rawTags.map((t) => (
              <span key={t} className="ma-chip">{t}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
